import { useEffect } from 'react';
import { BLOCK_REGISTRY } from './registry';
import { DEFAULT_LOCALE } from '@/i18n/config';
import { resolveLocalizedTree } from '@/utils/localize';

const FAQ_SCRIPT_ID = 'ld-faqpage';

/**
 * Renderizza una pagina a partire da un array di blocchi (src/content/pages.json).
 * I campi tradotti (i18n:true in registry.js) sono salvati come { it, en, ... }
 * e vengono risolti qui, per `locale`, prima di passarli al componente blocco.
 *
 * Gerarchia H1/H2/H3: calcolata qui, dalla posizione dei blocchi in pagina —
 * mai cablata nel componente blocco (vedi `headingField` in registry.js). Il
 * primo blocco con un titolo non vuoto tra quelli con `headingField` diventa
 * l'H1 della pagina, tutti gli altri sono H2 (le voci annidate, es. card di
 * una griglia, un livello sotto). Se nessun blocco produce un H1 (pagina
 * vuota o costruita solo con blocchi senza titolo prominente), viene
 * aggiunto un H1 invisibile (sr-only, letto da Google e screen reader) col
 * titolo della pagina — così ogni pagina ha sempre esattamente un H1, mai
 * zero, mai due.
 *
 * Se la pagina contiene un blocco FAQ con domande/risposte compilate, genera
 * anche il JSON-LD `FAQPage` (schema.org) — abilita il rich snippet Google.
 * @param {{ sections: Array<{ id: string, type: string, props?: object }>, locale?: string, pageTitle?: string }} props
 */
export function BlockRenderer({ sections = [], locale = DEFAULT_LOCALE, pageTitle = '' }) {
  let h1Assigned = false;
  const faqEntries = [];

  const blocks = sections.map((section) => {
    const entry = BLOCK_REGISTRY[section.type];
    if (!entry) {
      if (import.meta.env?.DEV) console.warn(`BlockRenderer: tipo blocco sconosciuto "${section.type}"`);
      return null;
    }
    const Block = entry.component;
    const props = resolveLocalizedTree({ ...entry.defaultProps, ...section.props }, locale);

    let headingLevel = 2;
    if (entry.headingField && !h1Assigned && (props[entry.headingField] || '').trim()) {
      headingLevel = 1;
      h1Assigned = true;
    }

    if (section.type === 'faq') {
      (props.items || []).forEach((item) => {
        if ((item.question || '').trim() && (item.answer || '').trim()) {
          faqEntries.push({ question: item.question.trim(), answer: item.answer.trim() });
        }
      });
    }

    return (
      <div key={section.id} id={section.id} data-block-type={section.type}>
        <Block {...props} headingLevel={headingLevel} />
      </div>
    );
  });

  useEffect(() => {
    if (faqEntries.length === 0) {
      document.getElementById(FAQ_SCRIPT_ID)?.remove();
      return;
    }
    const data = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqEntries.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    };
    let el = document.getElementById(FAQ_SCRIPT_ID);
    if (!el) {
      el = document.createElement('script');
      el.type = 'application/ld+json';
      el.id = FAQ_SCRIPT_ID;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
    return () => { document.getElementById(FAQ_SCRIPT_ID)?.remove(); };
    // faqEntries è derivato deterministicamente da sections/locale — usarlo
    // come dep (nuovo array a ogni render) farebbe girare l'effetto sempre.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, locale]);

  if (!h1Assigned && pageTitle) {
    blocks.unshift(<h1 key="sr-h1" className="sr-only">{pageTitle}</h1>);
  }

  return blocks;
}
